<template>
    <v-container fluid class="pa-4 leave-request-page" style="height: 85dvh; overflow-y: auto">
        <!-- Browse state -->
        <div v-if="!requestLeaveState">
            <div class="d-flex align-center justify-space-between mb-4 flex-wrap" style="gap: 12px">
                <div>
                    <span class="text-h5 font-weight-bold d-flex align-center">
                        <v-icon left color="primary">{{ isWfhMode ? 'mdi-home-account' : 'mdi-calendar-heart' }}</v-icon>
                        {{ isWfhMode ? 'Work From Home Requests' : 'Leave Requests' }}
                    </span>
                    <p class="text-body-2 grey--text mb-0 mt-1">
                        {{ isWfhMode ? 'Request and track your work-from-home days (these never use leave credits)' : 'Request and track your time off' }}
                    </p>
                </div>
                <v-btn color="primary" class="text-none" @click.stop="openLeaveRequestDialog">
                    <v-icon left small>mdi-plus</v-icon>
                    {{ isWfhMode ? 'Request WFH' : 'Request Leave' }}
                </v-btn>
            </div>

            <!-- Leave credit balances (not shown for WFH — it has no allowance) -->
            <v-row v-if="!isWfhMode && isLoadingCredits" class="mb-1">
                <v-col v-for="n in 2" :key="n" cols="12" sm="6" md="4">
                    <v-skeleton-loader type="list-item-two-line" />
                </v-col>
            </v-row>
            <v-row v-else-if="!isWfhMode && leavesCredits.length > 0" class="mb-1">
                <v-col v-for="(leave, leaveIndex) in leavesCredits" :key="leaveIndex" cols="12" sm="6" md="4">
                    <v-card outlined class="pa-3 balance-card">
                        <div class="d-flex align-center mb-2">
                            <!-- <v-icon color="primary" class="mr-2" small>{{ leave.icon || 'mdi-beach' }}</v-icon> -->
                            <span class="font-weight-bold text-body-2">Current Leave Balance</span>
                            <v-spacer />
                            <span class="text-caption grey--text">{{ leave.used_leaves }} / {{ leave.leave_count }} used</span>
                        </div>
                        <v-progress-linear color="primary" rounded height="8"
                            :value="(leave.used_leaves / leave.leave_count) * 100"></v-progress-linear>
                        <div class="text-caption font-weight-medium mt-1">
                            {{ leave.leave_count - leave.used_leaves }}
                            {{ leave.leave_count - leave.used_leaves === 1 ? "day" : "days" }} remaining
                        </div>
                    </v-card>
                </v-col>
            </v-row>
            <v-alert v-else-if="!isWfhMode" type="info" text dense outlined class="mb-1">
                You don't have allocated leave credits yet. Please reach out to the accounting team for assistance.
            </v-alert>

            <v-row class="mt-2">
                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="py-2 px-3">
                            <span class="font-weight-bold text-body-1">Pending Requests</span>
                            <v-spacer></v-spacer>
                            <v-chip small outlined>{{ pendingRequests.length }}</v-chip>
                        </v-card-title>
                        <v-divider />
                        <div class="scroll-panel">
                            <template v-if="isLoadingLeaves">
                                <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-two-line" class="pa-3" />
                            </template>
                            <div v-else-if="pendingRequests.length === 0" class="empty-state">
                                <v-icon color="grey lighten-1" size="40">mdi-calendar-check-outline</v-icon>
                                <p class="text-body-2 grey--text mb-0 mt-2">No pending requests</p>
                            </div>
                            <div v-else>
                                <div v-for="item in pendingRequests" :key="item.id" class="request-row">
                                    <div class="d-flex align-start justify-space-between" style="gap: 8px">
                                        <div style="min-width: 0">
                                            <div class="d-flex align-center flex-wrap" style="gap: 6px">
                                                <span class="font-weight-bold text-body-2">{{ item.leave_type?.name ?? "Leave" }}</span>
                                                <v-chip x-small label :color="getLeaveStatusColor(item.status?.id)" text-color="white">
                                                    <v-icon x-small left>{{ getLeaveStatusIcon(item.status?.id) }}</v-icon>
                                                    {{ item.status?.name || "Pending" }}
                                                </v-chip>
                                            </div>
                                            <div class="text-caption grey--text mt-1">
                                                {{ formatLeaveDateRange(item) }} &middot; {{ formatLeaveDuration(item) }}
                                            </div>
                                            <div class="note-row mt-1" v-if="item.note">
                                                <span :class="['note-text', { expanded: expandedNotes[item.id] }]">{{ item.note }}</span>
                                                <v-btn v-if="shouldShowExpandButton(item.note)" text x-small color="primary" class="pa-0"
                                                    style="min-width: auto; text-transform: none" @click="toggleNoteExpansion(item.id)">
                                                    {{ expandedNotes[item.id] ? "Show less" : "Show more" }}
                                                </v-btn>
                                            </div>
                                        </div>
                                        <div class="d-flex flex-shrink-0" style="gap: 2px">
                                            <v-btn icon x-small color="primary" @click="editRequest(item)">
                                                <v-icon small>mdi-pencil-outline</v-icon>
                                            </v-btn>
                                            <v-btn icon x-small color="error" @click="deleteRequest(item)">
                                                <v-icon small>mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="py-2 px-3">
                            <span class="font-weight-bold text-body-1">History</span>
                            <v-spacer></v-spacer>
                            <v-chip small outlined>{{ historyRequests.length }}</v-chip>
                        </v-card-title>
                        <v-divider />
                        <div class="scroll-panel">
                            <template v-if="isLoadingLeaves">
                                <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-two-line" class="pa-3" />
                            </template>
                            <div v-else-if="historyRequests.length === 0" class="empty-state">
                                <v-icon color="grey lighten-1" size="40">mdi-history</v-icon>
                                <p class="text-body-2 grey--text mb-0 mt-2">No past requests yet</p>
                            </div>
                            <div v-else>
                                <div v-for="item in historyRequests" :key="item.id" class="request-row">
                                    <div class="d-flex align-center flex-wrap" style="gap: 6px">
                                        <span class="font-weight-bold text-body-2">{{ item.leave_type?.name ?? "Leave" }}</span>
                                        <v-chip x-small label :color="getLeaveStatusColor(item.status?.id)" text-color="white">
                                            <v-icon x-small left>{{ getLeaveStatusIcon(item.status?.id) }}</v-icon>
                                            {{ item.status?.name }}
                                        </v-chip>
                                    </div>
                                    <div class="text-caption grey--text mt-1">
                                        {{ formatLeaveDateRange(item) }} &middot; {{ formatLeaveDuration(item) }}
                                    </div>
                                    <div v-if="item.reason" class="text-caption grey--text font-italic mt-1">
                                        Note from {{ (item.approver?.first_name ? `${item.approver.first_name} ${item.approver.last_name || ''}`.trim() : item.approver?.name) || "your approver" }}: {{ item.reason }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <!-- Request creation state -->
        <v-card v-else outlined>
            <v-card-title class="py-3 px-4">
                <span class="text-h6 font-weight-bold d-flex align-center">
                    <v-icon left color="primary">{{ isWfhMode ? 'mdi-home-account' : 'mdi-calendar-heart' }}</v-icon>
                    {{ isWfhMode ? 'Request Work From Home' : 'Request Leave' }}
                </span>
                <v-spacer></v-spacer>
                <v-btn outlined small class="text-none" @click.stop="cancelLeaveRequest">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-row>
                    <v-col cols="12" md="6">
                        <v-combobox v-if="!isWfhMode" v-model="leave_request.leave_type_id" :items="availableLeaveTypes"
                            item-id="id" item-text="name" label="Request Type" outlined dense hide-details>
                        </v-combobox>
                        <v-text-field v-else value="Work From Home" label="Request Type" outlined dense hide-details
                            readonly prepend-inner-icon="mdi-home-account"></v-text-field>
                        <v-alert v-if="isWfhMode && !wfhType" type="warning" dense text class="mt-2 mb-0 text-caption">
                            The "Work From Home" leave type isn't set up on this environment yet, so requests can't
                            be submitted. Ask an admin to run the leave-type seeder / latest migrations.
                        </v-alert>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-combobox v-model="leave_request.approver_id" :items="getApprovers" item-id="id"
                            item-text="approver_label" label="Approver" outlined dense hide-details>
                        </v-combobox>
                    </v-col>

                    <v-col cols="12">
                        <v-fade-transition>
                            <v-alert v-if="currentLeaveBalance" type="info" dense outlined class="mb-0">
                                <div class="text-caption">
                                    <strong>Current Leave Balance:</strong>
                                </div>
                                <v-progress-linear :value="(currentLeaveBalance.used / currentLeaveBalance.total) * 100"
                                    color="blue" rounded height="8" class="my-2"></v-progress-linear>
                                <div class="text-caption">
                                    <span class="font-weight-bold primary--text">{{ currentLeaveBalance.remaining }} days remaining</span>
                                    <span class="grey--text"> ({{ currentLeaveBalance.used }} / {{ currentLeaveBalance.total }} used)</span>
                                </div>
                            </v-alert>
                        </v-fade-transition>
                    </v-col>

                    <!-- Duration Type -->
                    <v-col cols="12">
                        <span class="text-caption font-weight-bold grey--text text--darken-1">DURATION</span>
                        <div class="mt-1">
                            <v-btn-toggle v-model="duration_type" mandatory dense color="primary" @change="onDurationTypeChange">
                                <v-btn value="full" small class="text-none">Full Day</v-btn>
                                <v-btn value="half" small class="text-none">Half Day</v-btn>
                            </v-btn-toggle>
                        </div>
                        <v-fade-transition>
                            <div v-if="duration_type === 'half'" class="mt-2">
                                <span class="text-caption font-weight-bold orange--text text--darken-2">PERIOD</span>
                                <div class="mt-1">
                                    <v-btn-toggle v-model="leave_request.half_day_period" mandatory dense color="orange">
                                        <v-btn value="AM" small class="text-none">Morning (AM)</v-btn>
                                        <v-btn value="PM" small class="text-none">Afternoon (PM)</v-btn>
                                    </v-btn-toggle>
                                </div>
                            </div>
                        </v-fade-transition>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-menu v-model="dateRange" :close-on-content-click="false" transition="scale-transition"
                            offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model="formattedDates"
                                    :label="duration_type === 'half' ? 'Select date' : 'Select date(s)'"
                                    prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense
                                    hide-details></v-text-field>
                            </template>
                            <v-date-picker :key="'date-picker-' + duration_type" v-model="datePickerModel"
                                :range="duration_type === 'full'" :multiple="false"
                                @input="handleDatePickerInput"></v-date-picker>
                        </v-menu>
                        <span class="text-caption grey--text">
                            {{ duration_type === 'half' ? 'Half-day leaves can only be requested for a single date' : 'You can select a date range for full-day leaves' }}
                        </span>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-fade-transition>
                            <v-alert v-if="leave_request.count !== null" :type="exceedsLeaveBalance ? 'error' : 'info'"
                                dense outlined class="mb-0">
                                <span class="text-caption">
                                    <strong>Duration:</strong>
                                    {{ duration_type === 'half' ? `0.5 day (${leave_request.half_day_period || 'Select AM/PM'})` : `${leave_request.count} day${leave_request.count > 1 ? 's' : ''}` }}
                                </span>                                <div v-if="exceedsLeaveBalance" class="mt-1 font-weight-bold error--text text-caption">
                                    <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                                    Insufficient balance! Only {{ currentLeaveBalance.remaining }} day{{ currentLeaveBalance.remaining !== 1 ? 's' : '' }} remaining.
                                </div>
                            </v-alert>
                        </v-fade-transition>
                    </v-col>

                    <v-col cols="12">
                        <v-textarea label="Leave Note" v-model="leave_request.note" outlined dense rows="3"
                            hide-details></v-textarea>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
                <v-spacer></v-spacer>
                <v-btn depressed :color="isFormValid ? 'primary' : 'grey'" :disabled="!isFormValid || isSubmitting"
                    :loading="isSubmitting" class="pl-5 pr-5 text-none white--text" @click.stop="requestALeaveFn">
                    <v-icon small class="mr-1">mdi-send</v-icon>
                    {{ isSubmitting ? "Submitting..." : "Send Request" }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Delete confirmation -->
        <v-dialog v-if="to_delete_data != null" v-model="to_delete_model" max-width="420">
            <v-card>
                <v-card-title class="text-h6">Confirm Deletion</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this <strong>{{ to_delete_data?.leave_type?.name }}</strong> request?
                    This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="to_delete_model = false; to_delete_data = null">Cancel</v-btn>
                    <v-btn color="red" dark @click="confirmDelete">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit dialog -->
        <v-dialog v-model="editLeaveDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>
                    <span class="text-h6">
                        <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
                        {{ isWfhMode ? 'Edit WFH Request' : 'Edit Leave Request' }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="cancelEditRequest">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-combobox v-if="!isWfhMode" v-model="edit_leave_request.leave_type_id" :items="leaveType"
                                item-id="id" item-text="name" label="Request Type" outlined dense hide-details>
                            </v-combobox>
                            <v-text-field v-else value="Work From Home" label="Request Type" outlined dense hide-details
                                readonly prepend-inner-icon="mdi-home-account"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-combobox v-model="edit_leave_request.approver_id" :items="getApprovers" item-id="id"
                                item-text="approver_label" label="Approver" outlined dense hide-details>
                            </v-combobox>
                        </v-col>

                        <v-col cols="12">
                            <span class="text-caption font-weight-bold grey--text text--darken-1">DURATION</span>
                            <div class="mt-1">
                                <v-btn-toggle v-model="edit_duration_type" mandatory dense color="primary"
                                    @change="onEditDurationTypeChange">
                                    <v-btn value="full" small class="text-none">Full Day</v-btn>
                                    <v-btn value="half" small class="text-none">Half Day</v-btn>
                                </v-btn-toggle>
                            </div>
                            <v-fade-transition>
                                <div v-if="edit_duration_type === 'half'" class="mt-2">
                                    <span class="text-caption font-weight-bold orange--text text--darken-2">PERIOD</span>
                                    <div class="mt-1">
                                        <v-btn-toggle v-model="edit_leave_request.half_day_period" mandatory dense color="orange">
                                            <v-btn value="AM" small class="text-none">Morning (AM)</v-btn>
                                            <v-btn value="PM" small class="text-none">Afternoon (PM)</v-btn>
                                        </v-btn-toggle>
                                    </div>
                                </div>
                            </v-fade-transition>
                        </v-col>

                        <v-col cols="12">
                            <v-menu v-model="editDateRange" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="editFormattedDates"
                                        :label="edit_duration_type === 'half' ? 'Select date' : 'Select date(s)'"
                                        prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined
                                        dense hide-details></v-text-field>
                                </template>
                                <v-date-picker :key="'edit-date-picker-' + edit_duration_type"
                                    v-model="editDatePickerModel" :range="edit_duration_type === 'full'"
                                    :multiple="false" @input="handleEditDatePickerInput"></v-date-picker>
                            </v-menu>
                            <v-fade-transition>
                                <v-alert v-if="edit_leave_request.count !== null" type="info" dense outlined class="mt-2 mb-0">
                                    <span class="text-caption">
                                        <strong>Duration:</strong>
                                        {{ edit_duration_type === 'half' ? `0.5 day (${edit_leave_request.half_day_period || 'Select AM/PM'})` : `${edit_leave_request.count} day${edit_leave_request.count > 1 ? 's' : ''}` }}
                                    </span>
                                </v-alert>
                            </v-fade-transition>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea label="Leave Note" v-model="edit_leave_request.note" outlined dense rows="3"
                                hide-details></v-textarea>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelEditRequest">Cancel</v-btn>
                    <v-btn color="primary" :disabled="!isEditFormValid || isSubmitting" :loading="isSubmitting"
                        @click="updateLeaveRequest">
                        <v-icon small class="mr-1">mdi-content-save</v-icon>
                        Update Request
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
/**vuex */
import { mapGetters } from "vuex";
/**dayjs */
import dayjs from "dayjs";
/**shared helpers */
import {
    formatLeaveDateRange,
    formatLeaveDuration,
    getLeaveStatusColor,
    getLeaveStatusIcon,
    getCompanyManagerApprovers,
} from "./leaveOvertimeUtils";

const WFH_TYPE_NAME = "Work From Home";

export default {
    inject: ["users", "roleManagers"],
    props: {
        // "leave" (default) = normal leave requests, WFH hidden from the type
        // list. "wfh" = a dedicated Work From Home request flow: no type picker,
        // no balance, list filtered to WFH only.
        mode: { type: String, default: "leave" },
    },
    data() {
        return {
            requestLeaveState: false,
            leaves: [],
            leavesCredits: [],
            leaveType: [],
            isLoadingLeaves: true,
            isLoadingCredits: true,
            leave_request: {
                leave_type_id: null,
                approver_id: null,
                count: null,
                dates: [],
                from: null,
                to: null,
                note: null,
                is_half_day: false,
                half_day_period: null,
            },
            duration_type: "full", // 'full' or 'half'
            isSubmitting: false,
            dateRange: false,
            to_delete_data: null,
            to_delete_model: false,
            expandedNotes: {}, // Track which notes are expanded
            editLeaveDialog: false,
            edit_leave_request: {
                id: null,
                leave_type_id: null,
                approver_id: null,
                count: null,
                dates: [],
                from: null,
                to: null,
                note: null,
                is_half_day: false,
                half_day_period: null,
                leave_status_id: 1,
            },
            edit_duration_type: "full",
            editDateRange: false,
        };
    },
    async mounted() {
        await Promise.all([this.getLeaves(), this.getLeaveCredits(), this.getLeaveTypes()]);
    },
    computed: {
        ...mapGetters({
            user: "auth/get_user",
            get_roles: "auth/get_roles",
        }),
        getApprovers() {
            return getCompanyManagerApprovers(this.users(), this.user, this.roleManagers ? this.roleManagers() : [], this.get_roles);
        },
        isWfhMode() {
            return this.mode === "wfh";
        },
        // The "Work From Home" leave type object, once /api/leave/type has loaded.
        wfhType() {
            return this.leaveType.find((t) => (t.name || "").trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase()) || null;
        },
        isWfhRequestItem() {
            return (item) => (item.leave_type && item.leave_type.name ? item.leave_type.name : "").trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase();
        },
        availableLeaveTypes() {
            // Check if user has Accumulated Vacation Leave (ID: 6)
            const hasAccumulatedVacation = this.leavesCredits.some((credit) => credit.leave_type_id === 6);

            return this.leaveType.filter((leaveType) => {
                // Work From Home has its own tab — never offered in the leave form.
                if ((leaveType.name || "").trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase()) {
                    return false;
                }

                // Hide Vacation Leave (ID: 1) if user has Accumulated Vacation Leave
                if (leaveType.id === 1 && hasAccumulatedVacation) {
                    return false;
                }

                // For Accumulated Vacation Leave (ID: 6), only show if user has credits
                if (leaveType.id === 6) {
                    return hasAccumulatedVacation;
                }

                // Show all other leave types
                return true;
            });
        },
        // Requests for this tab: WFH-only in wfh mode, everything-but-WFH otherwise.
        scopedLeaves() {
            return this.leaves.filter((q) => this.isWfhRequestItem(q) === this.isWfhMode);
        },
        pendingRequests() {
            return this.scopedLeaves
                .filter((q) => q.leave_status_id === 1)
                .sort((a, b) => b.id - a.id);
        },
        historyRequests() {
            return this.scopedLeaves
                .filter((q) => [2, 3].includes(q.leave_status_id))
                .sort((a, b) => b.id - a.id);
        },
        formattedDates() {
            return this.formatDatesForDisplay(this.leave_request.dates, this.duration_type, this.leave_request.half_day_period);
        },
        // The full leave type object currently chosen in the request form.
        selectedRequestType() {
            const chosen = this.leave_request.leave_type_id;
            if (!chosen) return null;
            if (typeof chosen === "object") return chosen;
            return this.leaveType.find((t) => t.id === chosen) || null;
        },
        currentLeaveBalance() {
            // WFH has no allowance at all.
            if (this.isWfhMode) {
                return null;
            }

            // Check if a leave type is selected
            if (!this.leave_request.leave_type_id) {
                return null;
            }

            // Types that never draw from a credit pool (Work From Home, Unpaid
            // Leave) have no balance to show or check.
            if (this.selectedRequestType && this.selectedRequestType.deducts_credit === false) {
                return null;
            }

            // Handle both object and ID formats
            // let leaveTypeId;
            // if (typeof this.leave_request.leave_type_id === "object" && this.leave_request.leave_type_id.id) {
            //     leaveTypeId = this.leave_request.leave_type_id.id;
            // } else if (typeof this.leave_request.leave_type_id === "number") {
            //     leaveTypeId = this.leave_request.leave_type_id;
            // } else {
            //     return null;
            // }

            // Check if it's Vacation Leave (id: 1) or Accumulated Vacation Leave (id: 6)
            // if (![1, 6].includes(leaveTypeId)) {
            //     return null;
            // }

            // Find the leave credits for this leave type
            const leaveCredit = this.leavesCredits.find((lc) => lc.leave_type_id === 1);

            if (!leaveCredit) {
                return null;
            }

            return {
                total: leaveCredit.leave_count || 0,
                used: leaveCredit.used_leaves || 0,
                remaining: (leaveCredit.leave_count || 0) - (leaveCredit.used_leaves || 0),
                leaveTypeName: leaveCredit.leave_type?.name || "Leave",
            };
        },
        exceedsLeaveBalance() {
            if (!this.currentLeaveBalance || !this.leave_request.count) {
                return false;
            }
            return this.leave_request.count > this.currentLeaveBalance.remaining;
        },
        isFormValid() {
            const baseValid = !!(
                this.leave_request.leave_type_id &&
                this.leave_request.approver_id &&
                this.leave_request.count &&
                this.leave_request.note &&
                this.leave_request.from &&
                this.leave_request.to
            );

            if (this.exceedsLeaveBalance) {
                return false;
            }

            if (this.duration_type === "half") {
                return baseValid && !!this.leave_request.half_day_period;
            }

            return baseValid;
        },
        datePickerModel: {
            get() {
                // For half-day (range=false), return single string
                if (this.duration_type === "half") {
                    return Array.isArray(this.leave_request.dates) && this.leave_request.dates.length > 0
                        ? this.leave_request.dates[0]
                        : null;
                }
                // For full-day (range=true), return array
                return Array.isArray(this.leave_request.dates) ? this.leave_request.dates : [];
            },
            set(value) {
                // Always store as array internally for consistency
                if (this.duration_type === "half") {
                    this.leave_request.dates = value ? [value] : [];
                } else {
                    this.leave_request.dates = Array.isArray(value) ? value : value ? [value] : [];
                }
            },
        },
        editDatePickerModel: {
            get() {
                if (this.edit_duration_type === "half") {
                    return Array.isArray(this.edit_leave_request.dates) && this.edit_leave_request.dates.length > 0
                        ? this.edit_leave_request.dates[0]
                        : null;
                }
                return Array.isArray(this.edit_leave_request.dates) ? this.edit_leave_request.dates : [];
            },
            set(value) {
                if (this.edit_duration_type === "half") {
                    this.edit_leave_request.dates = value ? [value] : [];
                } else {
                    this.edit_leave_request.dates = Array.isArray(value) ? value : value ? [value] : [];
                }
            },
        },
        editFormattedDates() {
            return this.formatDatesForDisplay(this.edit_leave_request.dates, this.edit_duration_type, this.edit_leave_request.half_day_period);
        },
        isEditFormValid() {
            const baseValid = !!(
                this.edit_leave_request.leave_type_id &&
                this.edit_leave_request.approver_id &&
                this.edit_leave_request.count &&
                this.edit_leave_request.note &&
                this.edit_leave_request.from &&
                this.edit_leave_request.to
            );

            if (this.edit_duration_type === "half") {
                return baseValid && !!this.edit_leave_request.half_day_period;
            }

            return baseValid;
        },
    },
    watch: {
        // In WFH mode the request type is locked to the "Work From Home" leave
        // type. The form can be opened before /api/leave/type has resolved (the
        // WFH tab lazy-mounts this component), leaving leave_type_id null and the
        // Send button permanently disabled. Backfill it once the type is known.
        wfhType: {
            handler(type) {
                if (this.isWfhMode && type && !this.leave_request.leave_type_id) {
                    this.leave_request.leave_type_id = type;
                }
            },
            immediate: true,
        },
        "edit_leave_request.dates": {
            handler(newDates) {
                this.applyDatesToRequest(newDates, this.edit_duration_type, this.edit_leave_request, "resetEditLeaveRequest");
            },
            deep: true,
        },
        "leave_request.dates": {
            handler(newDates) {
                this.applyDatesToRequest(newDates, this.duration_type, this.leave_request, "resetLeaveRequest");
            },
            deep: true,
        },
    },
    methods: {
        formatDatesForDisplay(dates, durationType, halfDayPeriod) {
            if (!dates) {
                return "";
            }

            let datesArray;
            if (Array.isArray(dates)) {
                datesArray = dates;
            } else if (typeof dates === "string") {
                datesArray = [dates];
            } else {
                return "";
            }

            const validDates = datesArray.filter((date) => date && typeof date === "string" && dayjs(date).isValid());

            if (validDates.length === 0) {
                return "";
            }

            if (durationType === "half") {
                const formattedDate = dayjs(validDates[0]).format("YYYY-MM-DD");
                const period = halfDayPeriod ? ` (${halfDayPeriod})` : "";
                return `${formattedDate}${period}`;
            }

            if (validDates.length === 2) {
                const date1 = dayjs(validDates[0]).format("YYYY-MM-DD");
                const date2 = dayjs(validDates[1]).format("YYYY-MM-DD");
                return `${date1} - ${date2}`;
            } else if (validDates.length === 1) {
                return dayjs(validDates[0]).format("YYYY-MM-DD");
            }
            return "";
        },
        applyDatesToRequest(newDates, durationType, requestObject, resetMethodName) {
            if (!newDates) {
                this[resetMethodName]();
                return;
            }

            let datesArray = Array.isArray(newDates) ? newDates : [newDates];
            datesArray = datesArray.filter((date) => date && typeof date === "string");

            if (datesArray.length === 0) {
                this[resetMethodName]();
                return;
            }

            if (durationType === "half") {
                const selectedDate = datesArray[0];

                if (selectedDate && dayjs(selectedDate).isValid()) {
                    requestObject.from = selectedDate;
                    requestObject.to = selectedDate;
                    requestObject.count = 0.5;

                    if (datesArray.length > 1) {
                        this.$nextTick(() => {
                            this.$set(requestObject, "dates", [selectedDate]);
                        });
                    }
                } else {
                    this[resetMethodName]();
                }
                return;
            }

            if (datesArray.length === 2) {
                const date1 = dayjs(datesArray[0]);
                const date2 = dayjs(datesArray[1]);

                if (!date1.isValid() || !date2.isValid()) {
                    this[resetMethodName]();
                    return;
                }

                const startDate = date1.isBefore(date2) ? date1 : date2;
                const endDate = date1.isBefore(date2) ? date2 : date1;

                requestObject.from = startDate.format("YYYY-MM-DD");
                requestObject.to = endDate.format("YYYY-MM-DD");

                // Every calendar day counts, weekends included.
                requestObject.count = endDate.startOf("day").diff(startDate.startOf("day"), "day") + 1;
            } else if (datesArray.length === 1) {
                const selectedDate = dayjs(datesArray[0]);

                if (!selectedDate.isValid()) {
                    this[resetMethodName]();
                    return;
                }

                requestObject.from = selectedDate.format("YYYY-MM-DD");
                requestObject.to = selectedDate.format("YYYY-MM-DD");

                requestObject.count = 1;
            } else {
                this[resetMethodName]();
            }
        },
        resetLeaveRequest() {
            this.leave_request.from = null;
            this.leave_request.to = null;
            this.leave_request.count = null;
        },
        resetEditLeaveRequest() {
            this.edit_leave_request.from = null;
            this.edit_leave_request.to = null;
            this.edit_leave_request.count = null;
        },
        handleDatePickerInput(value) {
            this.datePickerModel = value;

            // A range picker fires @input on the FIRST date click too (value is
            // then a 1-element array). Closing the menu there makes picking an
            // end date impossible — only close once the selection is complete.
            const complete =
                this.duration_type === "full"
                    ? Array.isArray(value) && value.length === 2
                    : !!value;

            if (complete) {
                this.dateRange = false;
            }
        },
        onDurationTypeChange() {
            this.leave_request.dates = [];
            this.leave_request.from = null;
            this.leave_request.to = null;
            this.leave_request.count = null;
            this.leave_request.is_half_day = this.duration_type === "half";

            if (this.duration_type === "full") {
                this.leave_request.half_day_period = null;
            }

            this.dateRange = false;
        },
        formatLeaveDateRange,
        formatLeaveDuration,
        getLeaveStatusColor,
        getLeaveStatusIcon,
        shouldShowExpandButton(note) {
            // Show expand button if note is longer than 100 characters
            return note && note.length > 100;
        },
        toggleNoteExpansion(itemId) {
            const keyId = String(itemId);
            this.$set(this.expandedNotes, keyId, !this.expandedNotes[keyId]);
        },
        openLeaveRequestDialog() {
            // WFH mode has no type picker — lock the request to the WFH type.
            if (this.isWfhMode) {
                this.leave_request.leave_type_id = this.wfhType;
            }
            this.requestLeaveState = true;
        },
        cancelLeaveRequest() {
            this.requestLeaveState = false;
            this.duration_type = "full";
            this.isSubmitting = false;
            this.leave_request = {
                leave_type_id: null,
                approver_id: null,
                count: null,
                dates: [],
                from: null,
                to: null,
                note: null,
                is_half_day: false,
                half_day_period: null,
            };
        },
        async requestALeaveFn() {
            this.isSubmitting = true;

            if (
                !this.leave_request.leave_type_id ||
                !this.leave_request.approver_id ||
                !this.leave_request.count ||
                !this.leave_request.note
            ) {
                this.$toast.error("Please fill out the form");
                this.isSubmitting = false;
                return;
            }

            if (this.duration_type === "half") {
                if (!this.leave_request.half_day_period) {
                    this.$toast.error("Please select AM or PM for half-day leave");
                    this.isSubmitting = false;
                    return;
                }
                if (this.leave_request.count !== 0.5) {
                    this.$toast.error("Half-day leave count should be 0.5");
                    this.isSubmitting = false;
                    return;
                }
            }

            try {
                const tp = {
                    leave_type_id: this.leave_request.leave_type_id.id,
                    approver_id: this.leave_request.approver_id.id,
                    count: this.leave_request.count,
                    from: this.leave_request.from,
                    to: this.leave_request.to,
                    note: this.leave_request.note,
                    is_half_day: this.leave_request.is_half_day,
                    half_day_period: this.leave_request.half_day_period,
                };
                const response = await this.requestALeave(tp);
                const { data } = response;
                if (data) {
                    this.$toast.success(
                        this.duration_type === "half"
                            ? `Half-day (${this.leave_request.half_day_period}) leave request submitted successfully!`
                            : "Leave request submitted successfully!"
                    );
                    await this.getLeaves();
                    this.cancelLeaveRequest();
                }
            } catch (error) {
                this.$toast.error("An error occurred while requesting leave.");
            } finally {
                this.isSubmitting = false;
            }
        },
        editRequest(item) {
            const leaveType = this.leaveType.find((lt) => lt.id === item.leave_type_id);
            // The saved approver may no longer be a company manager — keep them selectable
            let approver = this.getApprovers.find((a) => a.id === item.approver_id);
            if (!approver) {
                const u = this.users().find((q) => q.id === item.approver_id);
                approver = u ? { ...u, approver_label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name } : null;
            }

            this.edit_leave_request = {
                id: item.id,
                leave_type_id: leaveType || null,
                approver_id: approver || null,
                count: item.count,
                dates: item.is_half_day ? [item.from] : item.from === item.to ? [item.from] : [item.from, item.to],
                from: item.from,
                to: item.to,
                note: item.note,
                is_half_day: item.is_half_day,
                half_day_period: item.half_day_period,
                leave_status_id: item.leave_status_id || 1,
            };

            this.edit_duration_type = item.is_half_day ? "half" : "full";
            this.editLeaveDialog = true;
        },
        cancelEditRequest() {
            this.editLeaveDialog = false;
            this.edit_duration_type = "full";
            this.edit_leave_request = {
                id: null,
                leave_type_id: null,
                approver_id: null,
                count: null,
                dates: [],
                from: null,
                to: null,
                note: null,
                is_half_day: false,
                half_day_period: null,
                leave_status_id: 1,
            };
        },
        handleEditDatePickerInput(value) {
            this.editDatePickerModel = value;
            this.editDateRange = false;
        },
        onEditDurationTypeChange() {
            this.edit_leave_request.dates = [];
            this.edit_leave_request.from = null;
            this.edit_leave_request.to = null;
            this.edit_leave_request.count = null;
            this.edit_leave_request.is_half_day = this.edit_duration_type === "half";

            if (this.edit_duration_type === "full") {
                this.edit_leave_request.half_day_period = null;
            }

            this.editDateRange = false;
        },
        async updateLeaveRequest() {
            this.isSubmitting = true;

            if (
                !this.edit_leave_request.leave_type_id ||
                !this.edit_leave_request.approver_id ||
                !this.edit_leave_request.count ||
                !this.edit_leave_request.note
            ) {
                this.$toast.error("Please fill out the form");
                this.isSubmitting = false;
                return;
            }

            if (this.edit_duration_type === "half") {
                if (!this.edit_leave_request.half_day_period) {
                    this.$toast.error("Please select AM or PM for half-day leave");
                    this.isSubmitting = false;
                    return;
                }
                if (this.edit_leave_request.count !== 0.5) {
                    this.$toast.error("Half-day leave count should be 0.5");
                    this.isSubmitting = false;
                    return;
                }
            }

            try {
                const payload = {
                    leave_type_id: this.edit_leave_request.leave_type_id.id,
                    approver_id: this.edit_leave_request.approver_id.id,
                    count: this.edit_leave_request.count,
                    from: this.edit_leave_request.from,
                    to: this.edit_leave_request.to,
                    note: this.edit_leave_request.note,
                    is_half_day: this.edit_leave_request.is_half_day,
                    half_day_period: this.edit_leave_request.half_day_period,
                    leave_status_id: this.edit_leave_request.leave_status_id,
                    user_id: this.user.id,
                    is_updated: 0,
                };
                const response = await this.$axios.patch(`/api/leave/${this.edit_leave_request.id}`, payload);

                if (response.data) {
                    this.$toast.success(
                        this.edit_duration_type === "half"
                            ? `Half-day (${this.edit_leave_request.half_day_period}) leave request updated successfully!`
                            : "Leave request updated successfully!"
                    );
                    await this.getLeaves();
                    this.cancelEditRequest();
                }
            } catch (error) {
                this.$toast.error("An error occurred while updating leave request.");
            } finally {
                this.isSubmitting = false;
            }
        },
        deleteRequest(item) {
            this.to_delete_data = item;
            this.to_delete_model = true;
        },
        async confirmDelete() {
            try {
                await this.cancelRequest(this.to_delete_data.id);
                this.to_delete_data = null;
                this.to_delete_model = false;
            } catch (error) {
                console.log("error on deleting leave user request - component level", error);
            }
        },

        /**
         * get leaves of logged in user
         */
        async getLeaves() {
            this.isLoadingLeaves = true;
            try {
                const { data } = await this.$axios.get("/api/leave");
                this.leaves = data;
            } catch (error) {
                console.log("error on fetching leaves - service layer", error);
            } finally {
                this.isLoadingLeaves = false;
            }
        },

        /**
         * get leave types
         */
        async getLeaveTypes() {
            try {
                const { data } = await this.$axios.get("/api/leave/type");
                this.leaveType = data;
            } catch (error) {
                console.log("error on fetching leave types - service layer", error);
            }
        },

        /**
         * get users leave credits
         */
        async getLeaveCredits() {
            this.isLoadingCredits = true;
            try {
                const { data } = await this.$axios.get("/api/leave/credits");
                this.leavesCredits = data;
            } catch (error) {
                console.log("error occured on fetching leave credits - service layer", error);
            } finally {
                this.isLoadingCredits = false;
            }
        },

        /**
         * request a leave
         * @params
         * item?: object
         */
        async requestALeave(item) {
            try {
                return await this.$axios.post("/api/leave/request", item);
            } catch (error) {
                console.log("error on requesting a leave - service layer", error);
            }
        },

        /**
         * remove request
         * @params
         * record_id?: numeric
         */
        async cancelRequest(record_id) {
            try {
                await this.$axios.delete(`/api/leave/${record_id}`);
                await this.getLeaves();
            } catch (error) {
                console.log("error on deleting record leave use request - service layer", error);
            }
        },
    },
};
</script>

<style scoped>
.balance-card {
    height: 100%;
}

.scroll-panel {
    max-height: 48dvh;
    overflow-y: auto;
}

.request-row {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.request-row:last-child {
    border-bottom: none;
}

.empty-state {
    text-align: center;
    padding: 32px 16px;
}

.note-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.note-text {
    font-size: 0.8rem;
    color: #666;
    font-style: italic;
    line-height: 1.3;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.note-text.expanded {
    display: block;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    overflow: visible;
}
</style>
